import { getUserFromEvent } from "~/_server/auth.server";

// aws/server.dynamo.ts
import { ReturnValue, DynamoDBClient } from "@aws-sdk/client-dynamodb";
/* prettier-ignore */
import { DynamoDBDocumentClient, BatchGetCommand, QueryCommand,	UpdateCommand,DeleteCommand, GetCommand, PutCommand, QueryCommandInput, ScanCommand} from "@aws-sdk/lib-dynamodb";

import { REGION } from "./constants";
import { cleanStrings } from "~/lib";

export const RIASTABLE = "RIASRegs";

const getDynoClient = () => {
	"use server";

	const client = new DynamoDBClient({ region: REGION });

	return DynamoDBDocumentClient.from(client, {
		marshallOptions: { removeUndefinedValues: true },
	});
};

export const getCounter = async (sk: string): Promise<number> => {
	"use server";

	const client = getDynoClient();

	try {
		const params = {
			TableName: RIASTABLE,
			Key: { pk: "INC", sk },
			UpdateExpression: "ADD #count :inc",
			ExpressionAttributeNames: { "#count": "count" },
			ExpressionAttributeValues: { ":inc": 1 },
			ReturnValues: ReturnValue.UPDATED_NEW,
		};

		const res = await client.send(new UpdateCommand(params));

		return res.Attributes ? res.Attributes.count : 0;
	} catch (err) {
		console.log("@err", err);
		return -1;
	}
};

export const putItem = async (Item: Record<string, any>, TableName: string = RIASTABLE) => {
	"use server";

	return await runPutCommand({TableName,	Item: cleanStrings(Item)});
};


export const updateDB = async (
	pk: string,
	sk: string,
	rawUpdates: Record<string, any>,
) => {
	"use server";

	// Trim on save - every form in the app funnels through here.
	const updates = cleanStrings(rawUpdates);

	// Never echo the Xero token set into CloudWatch.
	//console.log("updateDB", { pk, sk, updates: pk === "XERO" ? "[redacted]" : updates });

	const updateKeys = Object.keys(updates);

	const UpdateExpression =
		"SET " + updateKeys.map((k, i) => `#k${i} = :v${i}`).join(", ");

	const ExpressionAttributeNames = updateKeys.reduce(
		(acc, k, i) => ({ ...acc, [`#k${i}`]: k }),
		{},
	);

	const ExpressionAttributeValues = updateKeys.reduce(
		(acc, k, i) => ({ ...acc, [`:v${i}`]: updates[k] }),
		{},
	);

	const params = {
		TableName: RIASTABLE,
		Key: { pk, sk },
		UpdateExpression,
		ExpressionAttributeNames,
		ExpressionAttributeValues,
		ReturnValues: "ALL_NEW", // return the updated record
	};

	const command = new UpdateCommand(params);

	//	console.log( "updateDB params", JSON.stringify( params ) );

	try {
		const client = getDynoClient();
		const res = await client.send(command);

		return res.Attributes;
	} catch (err) {
		console.error("updateDB failed", params, JSON.stringify(err));
		return { success: false, error: err };
	}
};

export const runScanCommand = async (cmd: any) => {
	"use server";

	try {
		const client = getDynoClient();
		return await client.send(new ScanCommand(cmd));
	} catch (err) {
		console.error("runScanCommand failed", cmd, JSON.stringify(err));
	}
};

export const runGetCommand = async (cmd: any) => {
	"use server";

	try {
		const client = getDynoClient();

		return await client.send(new GetCommand(cmd));
	} catch (err) {
		console.error("runQueryCommand failed", cmd, JSON.stringify(err));
	}
};

export const runDeleteCommand = async (
	pk: string,
	sk: string,
): Promise<{ error: string | null; statusCode: number }> => {
	"use server";

	let r;

	try {
		const client = getDynoClient();

		r = await client.send(
			new DeleteCommand({ TableName: RIASTABLE, Key: { pk, sk } }),
		);
	} catch (err) {
		console.error("runDeleteCommand failed", { pk, sk }, JSON.stringify(err));
	}

	return {
		error: r?.error ?? null,
		statusCode: r?.["$metadata"]?.httpStatusCode ?? -1,
	};
};

export const runQueryCommand = async (cmd: any) => {
	"use server";

	try {
		const client = getDynoClient();
		const result = await client.send(new QueryCommand(cmd));

		return {
			items: result.Items,
			nextKey: result.LastEvaluatedKey ?? null,
		};
	} catch (err) {
		console.error("runQueryCommand failed", cmd, JSON.stringify(err));
	}
};

export const runPutCommand = async (cmd: any) => {
	"use server";

	try {
		const client = getDynoClient();
		await client.send(new PutCommand(cmd));
	} catch (err) {
		console.error("runPutCommand failed", cmd, JSON.stringify(err));
	}
};

const chunk = (arr: Record<string, any>[], size: number) =>
	Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
		arr.slice(i * size, i * size + size),
	);

export const runBatchGetCommand = async (keys: Record<string, any>[], ProjectionExpression: string|undefined = undefined, ExpressionAttributeNames: Record<string, any>|undefined = undefined) => {
	"use server";

	const batches = chunk(keys, 100);
	const results = [];

	try {
		const client = getDynoClient();

		for (const batch of batches) {
			let request = { Keys: batch, ProjectionExpression, ExpressionAttributeNames };

			do {
				const res = await client.send(
					new BatchGetCommand({
						RequestItems: {
							[RIASTABLE]: request,
						},
					}),
				);

				results.push(...(res.Responses?.[RIASTABLE] || []));

				request = res.UnprocessedKeys?.[RIASTABLE];
			} while (request && request.Keys?.length);
		}

		return results;
	} catch (err) {
		console.error("runBatchGetCommand failed", keys, JSON.stringify(err));
	}
};

export const logAction = async (
	userId: number,
	group: RIASRecordType,
	action: LogAction,
	metadata: Record<string, any> = {},
	actor: string = "",
): Promise<void> => {
	"use server";

	const timestamp = new Date();

	try {
		await runPutCommand({
			TableName: RIASTABLE,
			Item: {
				pk: `USER#${userId}`,
				sk: `EVENT#${timestamp}#${action}`,
				actor: actor == "" ? `USER#${userId}` : actor,
				group,
				action,
				metadata,
				timestamp,
			},
		});
	} catch (err) {
		console.error("logAction failed", err);
	}
};

export const queryByPK = async (pkValue: string, sk: string) => {

	const result = await runQueryCommand( {
		TableName: RIASTABLE,
		KeyConditionExpression: "pk = :pk" + (sk ? " AND sk = :sk" : ""),
		ExpressionAttributeValues: {
			":pk": pkValue,
			...(sk ? { ":sk": sk } : {}),
		},
		ScanIndexForward: false,
	});

	return result?.items;
};

export const getProfile = async (profileId: number): Promise<any | null> => {
	"use server";

	if (profileId > 0) {
		const r = await runQueryCommand({
			TableName: RIASTABLE,
			KeyConditionExpression: "pk = :pk AND begins_with(sk, :prefix)",
			ExpressionAttributeValues: {
				":pk": `USER#${profileId}`,
				":prefix": "USER#",
			},
		});

		return r?.items?.[0] ?? {};
	}

	return null;
};

export const getByRiasIdDirect = async (riasId: any, limit = 25) => {
	"use server";

	return await runQueryCommand({
		TableName: RIASTABLE,
		IndexName: "gsiRiasId",
		KeyConditionExpression: "riasId = :riasId",
		ExpressionAttributeValues: {
			":riasId": parseInt(riasId.toString()),
		},
		ScanIndexForward: false,
		Limit: limit,
	});
};

export const getByUser = async (
	rowType: string,
	userId?: number,
	limit = 25,
	skId: string | undefined = undefined
) => {
	"use server";

	try {
		const user = userId && userId > 0 ? { userId } : await getUserFromEvent();

		if (!user) return { items: [], error: "unauthorised" };

		const args: QueryCommandInput = {
			TableName: RIASTABLE,
			ScanIndexForward: false,
		};

		if (!skId) {
			args.KeyConditionExpression = "pk = :pk AND begins_with(sk, :prefix)";
			args.ExpressionAttributeValues = {
				":pk": `USER#${user.userId}`,
				":prefix": `${rowType}#`,
			} as Record<string, any>;
		} else {
			args.KeyConditionExpression = "pk = :pk AND sk = :sk";
			args.ExpressionAttributeValues = {
				":pk": `USER#${user.userId}`,
				":sk": `${rowType}#${skId}`,
			} as Record<string, any>;
		}

		if (limit > -1) args.Limit = limit;

		const r = await runQueryCommand(args);

		return JSON.parse(JSON.stringify(r));
	} catch (err) {
		console.error("getByUser error", err);
		return { items: [], nextKey: null };
	}
};

export const batchGet = async (keys: Record<string, any>[]) => {
	"use server";

	try {
		const r = await runBatchGetCommand(keys);

		return r;
	} catch {
		return [];
	}
};
